// @ts-nocheck
/**
 * Shared live-scoring game card. Scorekeepers type the final score straight
 * on the card and tap Final (rle-score-game "complete"); Edit reopens it.
 * The chevron still opens the full game detail screen.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronRight, Loader2, Radio } from 'lucide-react';
import { getDivisionColor } from '@/utils/divisionColors';
import ScoreSheetImageLink from '@/components/tournament/ScoreSheetImageLink';

const PLACEHOLDER_RE = /^(winner|loser|w|l)\s+(of\s+)?(game|g|match|#)|^tbd\b|^seed\b|^bye$/i;

export function isTeamUndecided(name?: string | null, id?: string | null, hasIdField = false) {
  if (hasIdField && !id) return true;
  const n = (name || '').trim();
  return !n || PLACEHOLDER_RE.test(n);
}

const StatusPill: React.FC<{ status?: string }> = ({ status }) => {
  const s = status || 'scheduled';
  const map: Record<string, { label: string; cls: string }> = {
    in_progress: { label: 'Live', cls: 'bg-red-100 text-red-700' },
    completed: { label: 'Final', cls: 'bg-green-100 text-green-700' },
    cancelled: { label: 'Cancelled', cls: 'bg-muted text-muted-foreground' },
    scheduled: { label: 'Not started', cls: 'bg-muted text-muted-foreground' },
  };
  const m = map[s] || map.scheduled;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      {s === 'in_progress' && <Radio className="h-3 w-3" />}
      {m.label}
    </span>
  );
};

type CallScore = (gameId: string, payload: Record<string, any>) => Promise<{ game?: any }>;

interface Props {
  game: any;
  canScore: boolean;
  callScore: CallScore;
  onGameUpdated: (update: any) => void;
  onOpen?: () => void;
}

const toBox = (v: any) => (v === null || v === undefined ? '' : String(v));
const clean = (v: string) => v.replace(/[^0-9]/g, '').slice(0, 3);

const GameScoreCard: React.FC<Props> = ({ game, canScore, callScore, onGameUpdated, onOpen }) => {
  const c = getDivisionColor(game.division);
  const completed = game.status === 'completed';
  const cancelled = game.status === 'cancelled';
  const hasIdField = 'team_a_id' in game || 'team_b_id' in game;
  const undecided =
    isTeamUndecided(game.team_a, game.team_a_id, hasIdField) ||
    isTeamUndecided(game.team_b, game.team_b_id, hasIdField);

  const startsEmpty = !game.status || game.status === 'scheduled';
  const [a, setA] = useState(startsEmpty ? '' : toBox(game.score_a));
  const [b, setB] = useState(startsEmpty ? '' : toBox(game.score_b));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep boxes in sync when the game changes elsewhere (realtime / detail sheet).
  useEffect(() => {
    if (busy) return;
    if (completed || game.status === 'in_progress') {
      setA(toBox(game.score_a));
      setB(toBox(game.score_b));
    }
  }, [game.score_a, game.score_b, game.status]);

  const editable = canScore && !completed && !cancelled && !undecided;
  const bothSet = a !== '' && b !== '';
  const tied = bothSet && Number(a) === Number(b);
  const reason = !bothSet ? 'Enter both scores' : tied ? 'Scores are tied' : null;

  const submitFinal = async () => {
    if (reason || busy) return;
    setBusy(true);
    setError(null);
    const sa = Number(a), sb = Number(b);
    try {
      const res = await callScore(game.id, { action: 'complete', score_a: sa, score_b: sb });
      onGameUpdated({
        id: game.id,
        status: res?.game?.game_status ?? 'completed',
        score_a: res?.game?.score_a ?? sa,
        score_b: res?.game?.score_b ?? sb,
      });
    } catch (e: any) {
      setError(e?.message || 'Could not save the final score.');
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await callScore(game.id, { action: 'reopen' });
      setA(toBox(game.score_a));
      setB(toBox(game.score_b));
      onGameUpdated({ id: game.id, status: res?.game?.game_status ?? 'in_progress' });
    } catch (e: any) {
      setError(e?.message || 'Could not reopen the game.');
    } finally {
      setBusy(false);
    }
  };

  const ScoreCell = ({ value, set, label }: { value: string; set: (v: string) => void; label: string }) =>
    completed || cancelled || !canScore ? (
      <span className="w-16 text-right text-2xl font-extrabold tabular-nums text-primary">
        {completed ? value || '0' : value || '–'}
      </span>
    ) : (
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={label}
        value={value}
        disabled={!editable || busy}
        onChange={(e) => set(clean(e.target.value))}
        className="h-12 w-16 shrink-0 text-center text-xl font-bold tabular-nums"
      />
    );

  return (
    <div
      className="flex w-full items-stretch gap-2 border-b px-3 py-4 last:border-b-0"
      style={{ borderLeft: `5px solid ${c.border}` }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {game.start || 'TBD'} · {game.court || 'Court TBD'}
          </span>
          <span className="rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ backgroundColor: c.bg, color: c.text }}>
            {game.division}
          </span>
          <StatusPill status={game.status} />
        </div>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <p className="min-w-0 flex-1 break-words text-base font-semibold leading-tight text-foreground">{game.team_a}</p>
              {ScoreCell({ value: a, set: setA, label: `${game.team_a} score` })}
            </div>
            <div className="flex items-center gap-3">
              <p className="min-w-0 flex-1 break-words text-base font-semibold leading-tight text-foreground">{game.team_b}</p>
              {ScoreCell({ value: b, set: setB, label: `${game.team_b} score` })}
            </div>
          </div>

          {canScore && !cancelled && (
            <div className="w-full md:w-40 md:shrink-0">
              {completed ? (
                <div className="flex items-center justify-between gap-3 md:flex-col md:items-stretch">
                  <span className="inline-flex items-center justify-center rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
                    Final
                  </span>
                  <button
                    type="button"
                    onClick={reopen}
                    disabled={busy}
                    className="min-h-[44px] text-sm font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-50"
                  >
                    {busy ? 'Reopening…' : 'Edit'}
                  </button>
                </div>
              ) : undecided ? (
                <p className="text-sm text-muted-foreground">Waiting on earlier games</p>
              ) : (
                <>
                  <Button className="h-12 w-full text-base font-bold" disabled={!!reason || busy} onClick={submitFinal}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Final'}
                  </Button>
                  {reason && <p className="mt-1 text-center text-xs text-muted-foreground">{reason}</p>}
                </>
              )}
            </div>
          )}
        </div>

        {error && <p className="mt-2 text-sm font-medium text-destructive">{error}</p>}
        {game.score_sheet_image_url && (
          <div className="mt-1">
            <ScoreSheetImageLink url={game.score_sheet_image_url} />
          </div>
        )}
      </div>
      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          aria-label="Open game details"
          className="flex min-w-[44px] shrink-0 items-center justify-center rounded-md hover:bg-muted/40 active:bg-muted"
        >
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </button>
      )}
    </div>
  );
};

export default GameScoreCard;
